import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ClientDashboard from "@/pages/ClientDashboard";
import Performance from "@/pages/Performance";
import Funds from "@/pages/Funds";
import Cashflow from "@/pages/Cashflow";
import Transactions from "@/pages/Transactions";
import Documents from "@/pages/Documents";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AppLayout from "@/components/AppLayout";
import { Loader2 } from "lucide-react";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
    <Loader2 className="w-7 h-7 animate-spin text-[#0f291e]" strokeWidth={1.5} />
  </div>
);

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<RootRedirect />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/dashboard" element={<ClientDashboard />} />
        <Route path="/performance" element={<Performance />} />
        <Route path="/funds" element={<Funds />} />
        <Route path="/cashflow" element={<Cashflow />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/documents" element={<Documents />} />
      </Route>
      <Route element={<Protected adminOnly><AppLayout /></Protected>}>
        <Route path="/admin" element={<AdminClients />} />
        <Route path="/admin/clients/:id" element={<AdminClientDetail />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
